import { IAssessmentDashBoardConfigResponse } from '@publishing-engine/domain/types';
import { IGetSubmissionWorkflowResponse } from '@publishing-engine/submission-service/src/endpoints/api.response-types';
import { ReviewFormFields } from '@publishing-engine/submission-service/src/workflow-configuration/review-fields/review-form-fields';
import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import {
  fetchAssessmentDashBoardConfig,
  fetchAssessmentFilters,
  fetchAssessmentList,
  fetchFormSchema,
  getAssessmentListCount,
  ISubmissionSortOption
} from 'src/api/queries';
import { TABLE_PAGE_LIMIT } from 'src/constants';

export type IAssessmentFilterStatus = 'completed' | 'active';

export type IEarlyAssessmentListFilters = {
  journalId: Array<string>;
  expertRecommendations: Array<string>;
  workflowPhase: Array<string>;
  expertPhases: Array<string>;
  searchParams: string;
};
export type IEarlyAssessmentListFiltersResponse = {
  journals: Array<{ id: string; name: string; shortname: string }>;
  expertRecommendations: Array<string>;
  workflowPhases: Array<string>;
  expertPhases: Array<string>;
};

export function allKeysHaveValues(obj: any) {
  return Object.values(obj).every(
    value => value !== null && value !== undefined && value !== ''
  );
}

export function convertDataToLabel(str: string) {
  if (typeof str !== 'string') return '';
  return str
    .replace(/([A-Z])/g, ' $1') // Add space before capital letters
    .replace(/^./, char => char.toUpperCase()); // Capitalize first character
}

export const assessmentDefaultValues: IEarlyAssessmentListFilters = {
  journalId: [],
  workflowPhase: [],
  expertRecommendations: [],
  expertPhases: [],
  searchParams: ''
};

export const useAssessmentList = (
  status: IAssessmentFilterStatus,
  filters: IEarlyAssessmentListFilters,
  defaultFilters?: IAssessmentDashBoardConfigResponse,
  sort?: ISubmissionSortOption,
  limit: number = TABLE_PAGE_LIMIT
) => {
  const [page, setPage] = useState<number>(1);
  const offset = (page - 1) * limit;
  type FilterMap = Record<string, string[]>;
  let result: FilterMap = {};
  if (defaultFilters?.filters) {
    result = defaultFilters.filters.reduce((acc, filter) => {
      acc[filter.key] = filter.items.map(item => item.value);
      return acc;
    }, {} as FilterMap);
  }
  const filterPayload = {
    ...filters,
    workflowPhase: filters?.workflowPhase?.length
      ? filters.workflowPhase
      : result?.workflowPhase || [],
    expertPhases: filters?.expertPhases?.length
      ? filters?.expertPhases
      : result?.expertPhases || []
  };

  const { data, isLoading, isError } = useQuery({
    queryKey: [
      'assessment-list-data',
      { page, filterPayload, status, sort, limit }
    ],
    queryFn: () => fetchAssessmentList(offset, limit, filterPayload, sort),
    enabled: !!defaultFilters
  });

  const totalPages = Math.ceil((data?.total || 0) / limit);

  return {
    page,
    setPage,
    data,
    isLoading,
    isError,
    totalPages
  };
};

export const useAssessmentTotal = () => {
  return useQuery({
    queryKey: ['assessment-list-total'],
    queryFn: () => getAssessmentListCount()
  });
};

export const useAssessmentFilters = (filterStatus: IAssessmentFilterStatus) => {
  return useQuery({
    queryKey: ['assessment-filter', { filterStatus }],
    queryFn: () => fetchAssessmentFilters(filterStatus)
  });
};

export const useAssessmentDashBoardConfig = (
  view: string,
  workspaceId?: string
) =>
  useQuery({
    queryKey: ['submission-dashboard-config', { view, workspaceId }],
    queryFn: () => fetchAssessmentDashBoardConfig({ view, workspaceId })
  });

export const useOptInsData = (
  workflowData: IGetSubmissionWorkflowResponse,
  submissionContextId: string
) => {
  const firstAssessmentSchemaId =
    workflowData?.articleWorkflow?.formSchemas?.externalEarlyAssessment;
  const revisionAssessmentSchemaId =
    workflowData?.articleWorkflow?.formSchemas?.reviewerRevisionAssessment;

  const { data: assessmentFormSchema } = useQuery({
    queryKey: firstAssessmentSchemaId
      ? [
          'review-context',
          submissionContextId,
          'schema',
          firstAssessmentSchemaId
        ]
      : [],
    queryFn: () =>
      submissionContextId && firstAssessmentSchemaId
        ? fetchFormSchema(submissionContextId, firstAssessmentSchemaId)
        : undefined
  });

  const { data: revisionFormSchema } = useQuery({
    queryKey: revisionAssessmentSchemaId
      ? [
          'review-context',
          submissionContextId,
          'schema',
          revisionAssessmentSchemaId
        ]
      : [],
    queryFn: () =>
      submissionContextId && revisionAssessmentSchemaId
        ? fetchFormSchema(submissionContextId, revisionAssessmentSchemaId)
        : undefined
  });

  const formFieldData = revisionFormSchema?.documentSchema!;

  const reviewFormFields = new ReviewFormFields(formFieldData);
  const assessmentFormFields = assessmentFormSchema
    ? new ReviewFormFields(assessmentFormSchema?.documentSchema)
    : undefined;

  const config = {
    originality: reviewFormFields.getFieldConfig('Originality'),
    significance: reviewFormFields.getFieldConfig('Significance'),
    clarity: reviewFormFields.getFieldConfig('Clarity'),
    rigour: reviewFormFields.getFieldConfig('Rigour'),
    impact: reviewFormFields.getFieldConfig('Impact'),
    PeerReviewTransparency: assessmentFormFields?.getFieldConfig(
      'PeerReviewTransparency'
    ),
    ReviewerAnonymity:
      assessmentFormFields?.getFieldConfig('ReviewerAnonymity'),
    WebOfScienceOptIn: assessmentFormFields?.getFieldConfig('WebOfScienceOptIn')
  };

  const isOptIns =
    (config.PeerReviewTransparency && config.ReviewerAnonymity) ||
    config.WebOfScienceOptIn;

  return {
    config,
    isOptIns
  };
};
